import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { KeyRound, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

export function ModuleLocked({ module }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const contact = settings.vendor_contact;
  return (
    <div className="flex items-center justify-center p-6" data-testid="module-locked">
      <div className="pp-card max-w-lg w-full p-8 text-center space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700"><KeyRound size={28} /></div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Not included in your plan</h1>
        <p className="text-sm text-slate-600">The <strong>{module}</strong> module is not part of your current <strong>{settings.plan}</strong> plan.{user?.role === "master_admin" ? <> You can request it from <Link to="/admin" className="underline font-semibold">App Settings → Request modules</Link>.</> : " Ask your Master Admin to request it from the vendor."}</p>
        <VendorContact contact={contact} />
      </div>
    </div>
  );
}

export function useLicence() {
  return useQuery({ queryKey: ["licence"], queryFn: async () => (await api.get("/licence/status")).data, staleTime: 60_000 });
}

function VendorContact({ contact }) {
  if (!contact || !(contact.sales_email || contact.sales_phone)) return null;
  return <div className="text-xs text-slate-600 rounded-md border border-slate-200 bg-white p-3 text-left" data-testid="vendor-contact">{contact.sales_name && <div className="font-semibold">{contact.sales_name}</div>}{contact.sales_email && <div>Email: <a className="underline" href={`mailto:${contact.sales_email}`}>{contact.sales_email}</a></div>}{contact.sales_phone && <div>Phone: <a className="underline" href={`tel:${contact.sales_phone}`}>{contact.sales_phone}</a></div>}{contact.website && <div><a className="underline" href={contact.website} target="_blank" rel="noreferrer">{contact.website}</a></div>}</div>;
}

export function LicenceBanner() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const contact = settings.vendor_contact;
  const q = useLicence();
  const l = q.data;
  if (!l || !l.enforced) return null;
  const master = user?.role === "master_admin";
  if (l.locked) {
    if (master) return (
      <div className="rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex flex-wrap items-center gap-2" data-testid="trial-ended-master">
        <AlertTriangle size={16} /> <strong>Trial for {l.company} ended on {l.trial_ends}.</strong> Your users are locked out. Enter the licence key from your vendor in <Link to="/admin" className="underline font-semibold">App Settings</Link> to restore access.{contact?.sales_email && <span className="basis-full text-xs">Vendor: {contact.sales_name} · {contact.sales_email} · {contact.sales_phone}</span>}
      </div>);
    return (
      <div className="fixed inset-0 z-[60] bg-slate-50 flex items-center justify-center p-6" data-testid="trial-ended-lock">
        <div className="pp-card max-w-lg w-full p-8 text-center space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-rose-700"><AlertTriangle size={28} /></div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Trial ended</h1>
          <p className="text-sm text-slate-600">The trial period for <strong>{l.company}</strong> ended on {l.trial_ends}. Please contact your vendor to activate a licence — your data is safe and will be available again as soon as the licence is entered.</p>
          <VendorContact contact={contact} />
          <button className="pp-btn-ghost h-9 px-4" onClick={async () => { await logout(); window.location.href = "/login"; }} data-testid="trial-lock-logout">Sign out</button>
        </div>
      </div>);
  }
  if (!l.valid && !l.present && l.trial_days_left != null && l.trial_days_left <= 7) {
    return <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="trial-warning"><KeyRound size={14} /> Trial for <strong>{l.company}</strong> ends in {l.trial_days_left} day(s) ({l.trial_ends}). {master ? <>Ask your vendor for a licence key and enter it under <Link to="/admin" className="underline">App Settings</Link>.</> : "Contact your administrator."}</div>;
  }
  if (!l.valid && !l.present) return null;
  if (!l.valid) {
    return (
      <div className="rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex flex-wrap items-center gap-2" data-testid="licence-blocked">
        <AlertTriangle size={16} /> <strong>Licence {l.present ? (l.expired ? "expired" : "invalid") : "missing"}.</strong>
        {master ? <>Enter a valid licence key in <Link to="/admin" className="underline font-semibold">App Settings</Link> to restore access for all users.</> : "The application is locked — please contact your administrator."}
      </div>
    );
  }
  if (l.days_left <= 14) {
    return <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="licence-warning"><KeyRound size={14} /> Licence for <strong>{l.customer}</strong> expires in {l.days_left} day(s) ({l.expiry}).{master && <> Renew under <Link to="/admin" className="underline">App Settings</Link>.</>}</div>;
  }
  return null;
}
